// Выбор одной сущности — проекта, человека, аккаунта, строки: кнопка с точкой цвета или кружком человека
// и именем, список с поиском по буквам, стрелки, Enter, Escape. Один контрол на все места, где раньше стоял
// нативный select: у того нет ни цвета, ни поиска. action — выбор сразу действие (значение не хранится).
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Avatar } from './Avatar';
import { GRAY } from '../people';
import { useDismiss } from './dismiss';
import { POPOVER, cn } from './cn';
import { fieldInput } from './form';

export type EntityOption = {
  id: string; name: string;
  color?: string;            // точка цвета (проект, человек без картинки)
  avatar?: string | null;    // кружок человека: картинка аккаунта или инициалы на цвете
  person?: boolean;          // рисовать кружком, не точкой
  hint?: string;             // серым после имени (почта аккаунта)
  group?: string;            // заголовок группы в списке (дорожка у трека)
};

type Props = {
  value: string | null;
  options: EntityOption[];
  onChange: (id: string | null) => void;
  label: string;             // aria-label кнопки и списка; data-entity-select для e2e
  placeholder?: string;      // когда ничего не выбрано
  empty?: string;            // пункт «ничего» первым в списке (onChange(null)); нет — выбор обязателен
  disabled?: boolean;
  size?: 'md' | 'sm';
  action?: boolean;          // выбор — действие: кнопка всегда показывает placeholder
  footer?: (query: string, close: () => void) => ReactNode; // под списком: «+ Add «X»» и т.п.
  className?: string;
  data?: Record<string, string>;
};

const Swatch = ({ o, size }: { o: EntityOption; size: number }) => (
  o.person ? <Avatar name={o.name} color={o.color ?? GRAY} src={o.avatar} size={size} />
    : o.color ? <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: o.color }} aria-hidden /> : null
);

export function EntitySelect({ value, options, onChange, label, placeholder = '—', empty, disabled, size = 'md', action, footer, className, data }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);
  useDismiss(ref, open, close);
  const current = action ? undefined : options.find((o) => o.id === value);
  const q = query.trim().toLowerCase();
  const shown = useMemo(() => (q ? options.filter((o) => o.name.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q)) : options), [q, options]);
  // пункты списка по порядку: «ничего», потом варианты; active — индекс в этом ряду
  const rows = useMemo(() => [...(empty && !q ? [null] : []), ...shown], [empty, q, shown]);
  useEffect(() => { if (open) { search.current?.focus(); const i = rows.findIndex((o) => (o ? o.id : null) === value); setActive(Math.max(0, i)); } }, [open]);
  const pick = (id: string | null) => { onChange(id); close(); };
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const o = rows[Math.min(active, rows.length - 1)]; if (o !== undefined) pick(o ? o.id : null); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
  };
  const sm = size === 'sm';
  const swatch = sm ? 16 : 18;
  return (
    <div ref={ref} className={cn('relative', className)} data-entity-select={label} data-keeps-escape={open || undefined} {...data}>
      <button
        type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={label} disabled={disabled}
        className={cn(sm ? 'min-h-0 h-8 border border-line rounded px-2 text-sm bg-white w-full' : fieldInput, 'flex items-center gap-2 text-left disabled:opacity-60 disabled:cursor-not-allowed')}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => { if (!open && e.key.length === 1 && /\S/.test(e.key)) { setOpen(true); setQuery(e.key); e.preventDefault(); } }}
      >
        {current && <Swatch o={current} size={swatch} />}
        <span className={cn('flex-1 truncate', !current && 'text-gray-500')}>{current?.name ?? placeholder}</span>
        <ChevronDown className={cn('shrink-0 text-gray-500', sm ? 'w-3.5 h-3.5' : 'w-4 h-4')} aria-hidden />
      </button>
      {open && (
        <div className={cn('absolute left-0 top-full mt-1 z-50 min-w-full w-max max-w-80', POPOVER)} onKeyDown={onKey}>
          <input ref={search} className="min-h-0 w-full h-8 px-2 mb-1 border border-gray-200 rounded outline-none focus:border-accent" value={query} placeholder="Type to search…" aria-label={`Search ${label.toLowerCase()}`}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }} />
          <ul role="listbox" aria-label={label} className="max-h-64 overflow-y-auto scroller">
            {rows.map((o, i) => {
              const isActive = i === Math.min(active, rows.length - 1);
              if (o === null) {
                return <li key="__empty" role="option" aria-selected={value === null} className={cn('px-2 py-1.5 rounded cursor-default text-gray-500', isActive && 'bg-gray-100')} onMouseDown={(e) => { e.preventDefault(); pick(null); }} onMouseEnter={() => setActive(i)}>{empty}</li>;
              }
              const prev = rows[i - 1];
              const header = o.group && (!prev || prev === null || prev.group !== o.group) ? <li className="px-2 pt-1.5 pb-0.5 text-xs uppercase tracking-wide text-gray-500">{o.group}</li> : null;
              return (
                <Fragment key={o.id}>{header}
                <li role="option" aria-selected={o.id === value} data-entity-option={o.id}
                  className={cn('flex items-center gap-2 px-2 py-1.5 rounded cursor-default', isActive && 'bg-gray-100', o.id === value && 'font-semibold')}
                  onMouseDown={(e) => { e.preventDefault(); pick(o.id); }} onMouseEnter={() => setActive(i)}>
                  <Swatch o={o} size={swatch} /><span className="truncate">{o.name}</span>{o.hint && <span className="text-gray-500 truncate">{o.hint}</span>}
                </li></Fragment>
              );
            })}
            {rows.length === 0 && !footer && <li className="px-2 py-1.5 text-gray-500">No match</li>}
          </ul>
          {footer?.(query.trim(), close)}
        </div>
      )}
    </div>
  );
}
